import type { GameSoundConfig, GameSoundPack } from '@hypevault/shared-types';

export type SoundEffect =
  | 'game_start'
  | 'countdown_tick'
  | 'vote_received'
  | 'phase_change'
  | 'reveal'
  | 'correct'
  | 'incorrect'
  | 'winner'
  | 'loser'
  | 'gems_awarded'
  | 'dramatic_pause'
  | 'suspense_build'
  | 'celebration'
  /** HypePets — fires when an egg appears on the overlay. Registered
   *  to /sounds/egg-spawn.wav at module load. */
  | 'egg_spawn'
  /** HypePets 19f — wild encounter sounds. Default URLs registered
   *  at module load; per-streamer overrides arrive from the widget's
   *  fields.sounds map via the snapshot endpoint, applied at mount. */
  | 'wild_spawn'
  | 'wild_attack'
  /** Pass 28ai — fires when an !attack misses or the wild dodges.
   *  Audio cue the existing FX layer's MISS/DODGE stamp lacked. */
  | 'wild_miss'
  /** Pass 28ap — fires when a support cast lands a group heal or a
   *  HoT tick rolls. Differentiates support effects from the regular
   *  attack/miss cues. */
  | 'wild_heal'
  | 'wild_catch_success'
  | 'wild_catch_fail';

type PackMap = Partial<Record<SoundEffect, string>>;

const SOUND_PACKS: Record<GameSoundPack, PackMap> = {
  default: {},
  retro: {},
  dramatic: {},
  minimal: {},
};

/**
 * Stub sound manager. Assets land in apps/overlay-renderer/public/sounds/
 * later — until then, play() resolves the URL from SOUND_PACKS, no-ops with
 * a console log if the entry is empty, and decodes/plays via WebAudio when a
 * URL is registered. Configure once per widget mount, then play() at events.
 *
 * Why WebAudio + decoded buffers (not <audio>): chained widget events fire
 * faster than <audio> can rewind on Chrome; AudioBufferSourceNode is cheap
 * to instantiate per shot and decouples concurrent playback.
 */
class GameSoundManager {
  private context: AudioContext | null = null;
  private volume = 0;
  private pack: GameSoundPack = 'default';
  private cache = new Map<string, AudioBuffer>();
  /** 2026-06-21 — periodic resume retry handle. Started in
   *  configure() when the audio context lands in 'suspended' state and
   *  the host (OBS CEF, mostly) hasn't granted autoplay yet. Stops
   *  itself the moment the context flips to 'running'. */
  private resumeTimer: ReturnType<typeof setInterval> | null = null;

  configure(config: GameSoundConfig): void {
    this.volume = config.enabled ? Math.max(0, Math.min(1, config.volume / 100)) : 0;
    this.pack = config.pack;
    // 2026-06-21 — eagerly create the AudioContext on configure so
    // (a) unlock() called from a pointerdown / keydown listener has
    // something concrete to resume, and (b) we can start a periodic
    // resume retry below that catches the moment OBS grants audio.
    // Previously the context was lazily created inside play(), so the
    // first event's resume() attempt happened too late to ride a
    // user gesture in plain Chrome, AND OBS browser sources that
    // didn't fire any pointerdown event just sat suspended forever.
    if (this.volume > 0 && !this.context) {
      const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (Ctor) {
        try {
          this.context = new Ctor();
        } catch (err) {
          console.warn('[gameSounds] AudioContext construction failed', err);
        }
      }
    }
    // Kick off a resume-retry loop when needed. OBS allows autoplay
    // but only after the CEF page reports a media-engagement signal —
    // the .resume() call inside play() works most of the time, but
    // when the first event arrives during a quiet stream the user can
    // miss it entirely. Polling resume() every 2s until success means
    // sounds start firing the moment OBS lets them.
    if (this.context && this.context.state === 'suspended' && !this.resumeTimer) {
      this.resumeTimer = setInterval(() => {
        if (!this.context) {
          if (this.resumeTimer) clearInterval(this.resumeTimer);
          this.resumeTimer = null;
          return;
        }
        if (this.context.state === 'running') {
          if (this.resumeTimer) clearInterval(this.resumeTimer);
          this.resumeTimer = null;
          return;
        }
        void this.context.resume().catch(() => undefined);
      }, 2000);
    }
  }

  registerPackAsset(pack: GameSoundPack, effect: SoundEffect, url: string): void {
    SOUND_PACKS[pack][effect] = url;
  }

  async play(effect: SoundEffect): Promise<void> {
    if (this.volume === 0) {
      console.debug(`[gameSounds] play(${effect}) skipped — volume is 0 (configure not called?)`);
      return;
    }
    const url = SOUND_PACKS[this.pack][effect] ?? SOUND_PACKS.default[effect];
    if (!url) {
      console.debug(`[gameSounds] no asset registered for ${this.pack}/${effect}`);
      return;
    }
    try {
      if (!this.context) {
        // Fallback path — configure() normally creates the context
        // eagerly (2026-06-21), but a play() that races mount can
        // still land here. Construct on the fly.
        const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
        if (!Ctor) {
          console.warn('[gameSounds] AudioContext not available in this environment');
          return;
        }
        this.context = new Ctor();
      }
      // Browser autoplay policy: AudioContext starts in 'suspended'
      // state until the user has interacted with the page. resume() is
      // a no-op when already running but unblocks the first sound the
      // moment the policy allows it (OBS browser sources usually allow
      // autoplay; plain Chrome needs a click first).
      if (this.context.state === 'suspended') {
        try {
          await this.context.resume();
        } catch (err) {
          console.debug('[gameSounds] AudioContext resume() failed', err);
        }
      }
      // Even after resume() the context can stay suspended on OBS
      // until the media-engagement signal lands. Don't try to play a
      // buffer into a suspended context — Chromium silently drops it
      // and the cached buffer makes the next play() think it worked.
      // Skip cleanly; the periodic resume timer started in configure()
      // will catch the unlock and the next event will play.
      if (this.context.state !== 'running') {
        console.debug(
          `[gameSounds] context still ${this.context.state} after resume — dropping ${effect}`,
        );
        return;
      }
      let buffer = this.cache.get(url);
      if (!buffer) {
        const res = await fetch(url);
        if (!res.ok) {
          console.warn(`[gameSounds] asset fetch ${res.status} for ${url}`);
          return;
        }
        const arr = await res.arrayBuffer();
        buffer = await this.context.decodeAudioData(arr);
        this.cache.set(url, buffer);
      }
      const source = this.context.createBufferSource();
      const gain = this.context.createGain();
      source.buffer = buffer;
      gain.gain.value = this.volume;
      source.connect(gain);
      gain.connect(this.context.destination);
      source.start();
      console.debug(
        `[gameSounds] played ${effect} (state=${this.context.state}, vol=${this.volume})`,
      );
    } catch (err) {
      console.warn(`[gameSounds] play(${effect}) failed`, err);
    }
  }

  /** Manual resume helper — call this from a user-gesture event
   *  handler (click / keydown) on pages where autoplay is blocked.
   *  2026-06-21 — also creates the context if it doesn't exist yet,
   *  so a pointerdown that fires before any play() still primes the
   *  audio pipeline. Previously the early-return on null context made
   *  the listener pointless in that case. */
  async unlock(): Promise<void> {
    if (!this.context) {
      const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctor) return;
      try {
        this.context = new Ctor();
      } catch {
        return;
      }
    }
    if (this.context.state !== 'suspended') return;
    try {
      await this.context.resume();
    } catch (err) {
      console.debug('[gameSounds] unlock() failed', err);
    }
  }
}

export const gameSounds = new GameSoundManager();

// HypePets — egg-spawn sound. Vite serves /public/sounds/* at the root
// of the overlay-renderer build (overlays.hypevault.io/sounds/...).
// Registered once at module load so the first egg of any session plays
// (no widget-mount wiring required).
gameSounds.registerPackAsset('default', 'egg_spawn', '/sounds/egg-spawn.wav');

// HypePets 19f — wild encounter defaults. Re-use the existing alert
// pack so the sounds exist at deploy time even without operator setup.
// Streamers override per-event via /dashboard/widgets/hypeegg.
gameSounds.registerPackAsset('default', 'wild_spawn', '/sounds/egg-spawn.wav');
gameSounds.registerPackAsset('default', 'wild_attack', '/sounds/default-alert.wav');
gameSounds.registerPackAsset('default', 'wild_miss', '/sounds/ding.wav');
gameSounds.registerPackAsset('default', 'wild_heal', '/sounds/banner-alert.wav');
gameSounds.registerPackAsset('default', 'wild_catch_success', '/sounds/coin-rain.wav');
gameSounds.registerPackAsset('default', 'wild_catch_fail', '/sounds/ding.wav');

// 2026-06-21 — generic 'correct' / 'reveal' cues used by the HypePets
// fullscreen widget's Who's That HypePet? mini-game and any other
// widget that triggers them. Re-use the existing alert pack so the
// effects fire at deploy time without operator setup.
gameSounds.registerPackAsset('default', 'correct', '/sounds/coin-rain.wav');
gameSounds.registerPackAsset('default', 'reveal', '/sounds/banner-alert.wav');
